import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as uuid from 'uuid';
import { AsyncPipe } from '@angular/common';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { OPTIONS } from '../../../constants/options-list.constants';
import { DynamicProjectedTableComponent } from '../../../dynamic-projected-table/dynamic-projected-table.component';
import { ExpansionPanelComponent } from '../../../expansion-panel/expansion-panel.component';
import { PageLoadingComponent } from '../../../page-loading/page-loading.component';
import { PcoDetailsHeaderComponent } from './prime-pco-details-expansion-header/pco-details-header.component';
import { PcoDetailsExpansionContentComponent } from './prime-pco-details-expansion-content/pco-details-expansion-content.component';
import { PcoDetailMarkupLineItemComponent } from './pco-detail-markup-line-item/pco-detail-markup-line-item.component';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { StatusBlockComponent } from '@app/pages/webapp/projects/view-project/rfi-listing/status-block/status-block.component';

import { PcoDetailMarkupsHeaderComponent } from '@app/framework/interaction-bar/prime-commitments/pco-details/pco-detail-markup-line-item/pco-detail-line-item-header/pco-detail-markups-header.component';
import { CHANGE_ACTIONS_TYPE } from '@app/store/prime-commitments/prime-commitments.constants';
import { StyledOptionsListComponent } from '@app/framework/overlays/styled-options-list/styled-options-list.component';
import { IStyledOption } from '@app/framework/constants/documents.constants';
import { DriveService } from '@app/services/drive.service';
import { NotificationV2Service } from '@app/notification-v2.service';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { pcoSelectors } from '@app/store/pco/pco.selectors';
import { pcoActions } from '@app/store/pco/pco.actions';
import {
  PcoLogItem,
  PcoDetail,
  PCO_STATUS,
  PcoDetailItem,
  PcoDetailItemCost,
  PcoMarkup,
} from '@app/store/pco/pco.types';
import { AttachmentsDropdownComponent } from '@app/framework/file-handling/attachments-dropdown/attachments-dropdown.component';

@Component({
  selector: 'app-pco-details',
  imports: [
    StyledOptionsListComponent,
    StatusBlockComponent,
    DynamicProjectedTableComponent,
    ExpansionPanelComponent,
    PcoDetailsExpansionContentComponent,
    MoneyPipe,
    PageLoadingComponent,
    PcoDetailMarkupLineItemComponent,
    PcoDetailsHeaderComponent,
    AsyncPipe,
    PcoDetailMarkupsHeaderComponent,
    TooltipDirective,
    AttachmentsDropdownComponent,
  ],
  templateUrl: './pco-details.component.html',
  styleUrl: './prime-pco-details.common.scss',
})
export class PcoDetailsComponent implements OnInit, OnDestroy {
  @Input() PCOLog: PcoLogItem;
  @ViewChildren('pcoDetailHeader') pcoDetailHeader: QueryList<PcoDetailsHeaderComponent>;

  private readonly store = inject(Store);
  private readonly interactionBar = inject(InteractionBarStateService);
  private readonly notif = inject(NotificationV2Service);
  private readonly driveService = inject(DriveService);

  protected isLoading$ = this.store.select(pcoSelectors.selectIsPCODetailLoading);
  protected wasDataLoadedOnce$ = this.store.select(pcoSelectors.selectWasPCODataLoadedOnce);

  protected readonly markupLineItem$ = this.store.select(
    pcoSelectors.selectPCODetailsSidebarMarkupLineItems,
  );

  protected readonly OPTIONS = OPTIONS;

  editOptions: IStyledOption[] = [{ value: OPTIONS.EDIT }, { value: OPTIONS.DELETE }];
  PCO: PcoDetail = null;
  expandedItems = new Map<number, boolean>();
  editChangeActionTriggered = false;

  PCO$ = this.store.select(pcoSelectors.selectPCODetailsSidebar);
  combinedBudgetTotals$ = this.store.select(pcoSelectors.selectCombinedBudgetTotals);
  combinedCommitmentTotals$ = this.store.select(pcoSelectors.selectCombinedCommitmentTotals);
  isDestroyed$ = new Subject<void>();
  autoSave$ = this.store.select(pcoSelectors.selectPCOSidebarAutoSave);
  isAnyItemEdited$ = this.store.select(pcoSelectors.selectIsAnyItemBeingEdited);

  ngOnInit() {
    this.store.dispatch(pcoActions.onPCODetailsAccessed({ id: this.PCOLog.id }));
    this.PCO$.pipe(
      takeUntil(this.isDestroyed$),
      filter((PCO) => !!PCO),
    ).subscribe((data) => {
      this.PCO = DeepCopyService.deepCopy(data);
      //todo: create selector
      if (this.PCO.status !== 'closed') {
        this.editOptions = [
          { value: OPTIONS.EDIT },
          { value: OPTIONS.MARK_CLOSE },
          { value: OPTIONS.DELETE },
        ];
      } else {
        this.editOptions = [{ value: OPTIONS.EDIT }, { value: OPTIONS.DELETE }];
      }
      this.PCO.pco_items.forEach((item, index) => {
        this.expandedItems.set(index, this.expandedItems.get(index) ?? false);
      });
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
    this.store.dispatch(
      pcoActions.onPCODetailsSidebarClosed({
        editChangeActionTriggered: this.editChangeActionTriggered,
      }),
    );
  }

  registerOption(option: OPTIONS) {
    switch (option) {
      case OPTIONS.EDIT: {
        this.editChangeActionTriggered = true;
        this.interactionBar.openChangeAction(true, CHANGE_ACTIONS_TYPE.POTENTIAL_CHANGE_ORDER);
        break;
      }
      case OPTIONS.MARK_CLOSE: {
        this.PCO.status = PCO_STATUS.CLOSED;
        this.savePCO();
        break;
      }
      case OPTIONS.DELETE: {
        this.notif
          .showConfirmDialog('Are you sure you want to delete this PCO?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((response) => {
            if (response) {
              this.store.dispatch(pcoActions.onDeletePCO({ id: this.PCO.id }));
            }
          });
        break;
      }
    }
  }

  deleteUnmarkedLineItem(id: string) {
    const index = this.PCO.pco_items.findIndex((item) => item.id === id);
    if (index === -1) {
      return;
    }
    this.PCO.pco_items.splice(index, 1);
    this.savePCO();
  }

  deleteCost(item: PcoDetailItem, costIndex: number) {
    item.pco_item_costs.splice(costIndex, 1);
    this.savePCO();
  }

  addItem(option: OPTIONS.ADD_BUDGET_LINE | OPTIONS.ADD_SPEND_DESCRIPTION, item: PcoDetailItem) {
    switch (option) {
      case OPTIONS.ADD_SPEND_DESCRIPTION:
        item.pco_item_costs.push({
          id: uuid.v4(),
          pco_item_id: item.id,
          spend_description: 'Change description cost ' + (item.pco_item_costs.length + 1),
          budget_dollars: 0,
          commitment_dollars: 0,
          cco_id: null,
          created_at: '',
          updated_at: '',
          company_name: '',
          contract_title: '',
          pc_spend_description_id: null,
          editMode: true,
        });
        this.savePCO();
        break;
      case OPTIONS.ADD_BUDGET_LINE:
        this.addBudgetLine();
        break;
    }
  }

  addBudgetLine() {
    const id = uuid.v4();
    this.PCO.pco_items.push({
      id: id,
      pco_id: null,
      item_id: null,
      budget_dollars: 0,
      commitment_dollars: 0,
      created_at: '',
      updated_at: '',
      item_name: '',
      change_order_markup_rate: null,
      pco_item_costs: [],
      is_commitment_locked: false,
      is_budget_locked: false,
      division: '',
      cost_type: '',
      editMode: true,
    });

    this.expandedItems.set(this.PCO.pco_items.length - 1, true);

    this.store.dispatch(
      pcoActions.onPCOLineItemAdded({
        PCO: DeepCopyService.deepCopy(this.PCO),
      }),
    );
  }

  updatePCOCost(pcoItemCost: PcoDetailItemCost, pcoItem: PcoDetailItem, costIndex: number) {
    const index = this.PCO.pco_items.findIndex((item) => item.id === pcoItem.id);
    this.PCO.pco_items[index].pco_item_costs[costIndex] = pcoItemCost;
    this.PCO.pco_items[index].budget_dollars = this.PCO.pco_items[index].pco_item_costs.reduce(
      (acc, cost) => acc + cost.budget_dollars,
      0,
    );
    this.PCO.pco_items[index].commitment_dollars = this.PCO.pco_items[index].pco_item_costs.reduce(
      (acc, cost) => acc + cost.commitment_dollars,
      0,
    );

    // recalculate markups and save PCO - it's a bit messy, but markups are calculated in the store,
    // but the form data is local --- sorry mate, I don't want to refactor all of this
    // takeUntil / unsubscribe is not needed because of the take(1) operator
    this.updateMarkups().subscribe(() => {
      this.savePCO();
    });
  }

  isValid() {
    let allValid = true;
    Array.from(this.pcoDetailHeader).map((header) => {
      allValid = allValid && header.isValid();
    });

    return allValid;
  }

  savePCO() {
    this.updatePCOLocalState();

    setTimeout(() => {
      if (!this.isValid()) {
        this.store.dispatch(pcoActions.onPCOInvalid());
        return;
      }

      this.store.dispatch(
        pcoActions.onSyncPCODetailsTriggered({
          PCO: DeepCopyService.deepCopy(this.PCO),
        }),
      );
    }, 100);
  }

  updatePCOLocalState() {
    this.store.dispatch(
      pcoActions.onPCODetailsLocalStateUpdate({
        PCO: DeepCopyService.deepCopy(this.PCO),
      }),
    );
  }

  updateUnmarkedLineItem(pcoItem: PcoDetailItem, index: number) {
    this.PCO.pco_items[index] = {
      ...pcoItem,
      pco_item_costs: this.PCO.pco_items[index].pco_item_costs,
    };
    this.savePCO();
  }

  updateMarkupLineItem(pcoItem: PcoMarkup, index: number) {
    this.PCO.pco_markups[index] = pcoItem;
    this.savePCO();
  }

  onMarkupToggled() {
    if (this.PCO.markups_enabled) {
      // recalculate markups and save PCO
      this.updateMarkups().subscribe(() => {
        this.savePCO();
      });
    } else {
      this.savePCO();
    }
  }

  private updateMarkups() {
    // this is bad handling as it can be, I know, I know. Relax, brother.
    // But this component is messy as hell, and it would need a lot of refactoring to make it better.
    // The main problem is that it uses a local PCO and a stored PCO, and they're mixed together.
    // The markups are calculated in the store, but they won't be updated in the local PCO otherwise.
    return this.markupLineItem$.pipe(
      takeUntil(this.isDestroyed$),
      take(1),
      tap((markupLineItems) => {
        for (const selectorMarkupItem of markupLineItems) {
          const localMarkupItem = this.PCO.pco_markups.find(
            (localItem) => localItem.id === selectorMarkupItem.id,
          );
          if (!localMarkupItem) {
            console.warn('Markup item not found in local PCO', selectorMarkupItem);
            continue;
          }
          localMarkupItem.budget_dollars = selectorMarkupItem.budget_dollars;
          localMarkupItem.commitment_dollars = selectorMarkupItem.commitment_dollars;
        }
      }),
    );
  }
}
